import * as React from 'react';
import { usePopper } from 'react-popper';
import { isNode } from '../../dom';
import { useUpdateEffect } from '../../hooks';
import { Layer } from '../Helper';
import DropDownContext from './DropDownContext';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function reducer(state, action) {
  const firstIndex = 0;
  const lastIndex = state.itemsRefs.length - 1;
  switch (action.type) {
    case 'open':
      return {
        ...state,
        visible: true
      };
    case 'close':
      return {
        ...state,
        visible: false,
        index: -1
      };
    case 'toggle':
      return {
        ...state,
        visible: !state.visible
      };
    case 'openAtFisrtItem':
      return {
        ...state,
        visible: true,
        index: firstIndex
      };
    case 'openAtLastItem':
      return {
        ...state,
        visible: true,
        index: lastIndex
      };
    case 'selectItemAt':
      return {
        ...state,
        index: action.index
      };
    case 'selectFirstItem':
      return {
        ...state,
        index: firstIndex
      };
    case 'selectLastItem':
      return {
        ...state,
        index: lastIndex
      };
    case 'selectNextItem':
      {
        let next = state.index + 1;
        if (next > lastIndex) next = firstIndex;
        return {
          ...state,
          index: next
        };
      }
    case 'selectPrevItem':
      {
        let prev = state.index - 1;
        if (prev < firstIndex) prev = lastIndex;
        return {
          ...state,
          index: prev
        };
      }
    default:
      throw new Error();
  }
}
const initialState = {
  visible: false,
  index: -1
};
const defaultModifiers = [{
  name: 'offset',
  options: {
    offset: [0, 1]
  }
}];
function DropDownContainer({
  id,
  placement = 'auto',
  modifiers = defaultModifiers,
  renderReference,
  children,
  onChange
}) {
  const itemsRefs = React.useRef([]);
  const [referenceElement, setReferenceElement] = React.useState(null);
  const [popperElement, setPopperElement] = React.useState(null);
  const {
    styles,
    attributes
  } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [...modifiers]
  });
  const [state, dispatch] = React.useReducer(reducer, {
    ...initialState,
    itemsRefs: itemsRefs.current
  });
  useUpdateEffect(() => {
    if (typeof onChange === 'function') {
      onChange(state.visible);
    }
  }, [onChange, state.visible]);
  React.useEffect(() => {
    if (itemsRefs.current[state.index]) {
      itemsRefs.current[state.index].focus({
        preventScroll: true
      });
    }
  }, [state.index]);
  const handleClickOutside = React.useCallback(evt => {
    if (!state.visible) {
      return;
    }
    if (isNode(referenceElement) && referenceElement.contains(evt.target) || isNode(popperElement) && popperElement.contains(evt.target)) {
      return;
    }
    dispatch({
      type: 'close'
    });
  }, [popperElement, referenceElement, state.visible]);
  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside, false);
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, [handleClickOutside]);
  const handleClick = evt => {
    evt.preventDefault();
    dispatch({
      type: 'toggle'
    });
  };
  const handleKeydown = evt => {
    switch (evt.key) {
      case 'Enter':
      case 'SpaceBar':
      case ' ':
      case 'ArrowDown':
        evt.preventDefault();
        dispatch({
          type: 'openAtFisrtItem'
        });
        break;
      case 'ArrowUp':
        evt.preventDefault();
        dispatch({
          type: 'openAtLastItem'
        });
        break;
      case 'Escape':
        evt.preventDefault();
        dispatch({
          type: 'close'
        });
        break;
      default:
    }
  };
  const context = React.useMemo(() => ({
    ...state,
    dispatch,
    referenceElement
  }), [state, dispatch, referenceElement]);
  return /*#__PURE__*/_jsxs(DropDownContext.Provider, {
    value: context,
    children: [renderReference(mergeProps => ({
      'aria-haspopup': 'menu',
      'aria-controls': id,
      'aria-expanded': state.visible,
      innerRef: setReferenceElement,
      active: state.visible,
      onClick: handleClick,
      onKeyDown: handleKeydown,
      ...mergeProps
    }), state.visible), state.visible && /*#__PURE__*/_jsx(Layer, {
      children: children(({
        style,
        ...mergeProps
      } = {}) => ({
        'aria-labelledby': id,
        innerRef: setPopperElement,
        style: {
          ...style,
          ...styles.popper
        },
        ...attributes.popper,
        ...mergeProps
      }), state.visible)
    })]
  });
}
export default DropDownContainer;